import { REALTIME_SUBSCRIBE_STATES } from "@supabase/supabase-js";
import type { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase-client";
import type { Database } from "@/types/supabase";

export type ClientsSlice = {
  id: string;
  clientReady: boolean;
  clients: Database["public"]["Tables"]["clients"]["Row"][];
  clientSelf: Database["public"]["Tables"]["clients"]["Row"] | null;
  clientsSubscribe: () => () => void;
  clientAssignPodNumber: (podNumber?: string) => Promise<boolean>;
  clientUpdate: (onboarded: boolean) => Promise<boolean>;
};

export const createClientsSlice: StateCreator<
  ClientsSlice,
  [],
  [],
  ClientsSlice
> = (set, get) => {
  return {
    id: crypto.randomUUID(),
    clientReady: false,
    clients: [],
    clientSelf: null,
    clientsSubscribe: () => {
      const refreshClients = async () => {
        const { id } = get();
        const { data: clientsData } = await supabase
          .from("clients")
          .select("id, podNumber, onboarded");
        const allClients = clientsData ?? [];

        const clientSelf = allClients.find((m) => m.id === id) ?? null;
        const clients = allClients.filter((m) => m.id !== id);

        set({ clients, clientSelf, clientReady: true });
      };

      const channel = supabase.channel("clients-realtime");

      channel
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "clients" },
          async () => {
            await refreshClients();
          },
        )
        .subscribe(async (status) => {
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            await refreshClients();
          }
        });

      return () => {
        supabase.removeChannel(channel);
      };
    },
    clientAssignPodNumber: async (podNumber?: string) => {
      const { id } = get();
      const { data, error } = await supabase.rpc("client_assign_pod_number", {
        p_id: id,
        p_pod_number: podNumber ?? ("" as string),
      });

      if (error || !data) {
        return false;
      }

      return true;
    },
    clientUpdate: async (onboarded: boolean) => {
      const { id } = get();
      const { error } = await supabase
        .from("clients")
        .update({ onboarded })
        .eq("id", id);

      if (error) {
        return false;
      }

      return true;
    },
  };
};
